export const colours = {
  seafoam: '#34a5ac', // Primary colour
  lightGrey: '#F8F9FA', // Background colour
  darkTeal: '#04656B',
  lightTeal: '#84D4D8',
  axisGrey: '#949494',
  invalidGrey: '#BFBBC3',
  selectGrey: '#EEECF1',
  selectLightTeal: '#f7fbfc',
  selectBorderTeal: '#dfeaea',
  selectIsSelectedTeal: '#deeaea',
  selectHoverTeal: '#e9f2f2',
  selectOptionsText: '#333',
  selectIsSelectedText: '#000000',
  darkTeal2: '#3B506C',
  toggleBorderTeal: '#D9D7DD',
  disabledButton: '#e0e0e0',
  disabledButtonText: '#a6a6a6',
  backButtonGrey1: '#ECF0F1',
  formLabelGrey: '#26252e',
  warmGrey: '#9694A3',
  successGreen: '#19C449',
  alertRed: '#FA6B5B',
  fadedAlertRed: '#FFD7D3',
  predictionBlue: '#0131dd',
  predictionGhostGrey: '#97A4B5',
  preditionBlueLight: '#01b8dd',
  sonicSilver: '#757575'
};

/**  The values of this enum `AccessIntegrationName` are being used in backend to determine the integration type and perform integration specific tasks. */
export enum AccessIntegrationName {
  Wavelynx = 'bline', // Wavelynx integration name that backend uses
  AssaVisionline = 'AVL', // Assa Visionline integration name that backend uses
  Latch = 'Latch', // Latch integration name that backend uses
  WavelynxWallet = 'B-Line Wallet', // Wavelynx Wallet integration name that backend uses
  ICT = 'ICT', // ICT integration name
  None = 'None' // None integration is used when occupant doesn't have any integration
}

/**  The values of this enum `CardFormat` are being used to determine the integration type in few of the project's files. */
export enum CardFormat {
  AVL = 'AVL', // Assa Visionline Card Format
  W26 = 'W26', // Wavelynx Card Format
  WAVELYNXWALLET = 'B-Line Wallet', // Wavelynx Wallet Card Format
  ICT = 'ICT', // ICT Card Format
  LATCH = 'LATCH' // Latch Card Format
}

/**  The values of `cardFormatLabels` are being used as label names for the Card Formats in dropdown. */
export const cardFormatLabels: Record<CardFormat, string> = {
  [CardFormat.AVL]: 'Assa Visionline', // Assa Visionline Card Format label
  [CardFormat.W26]: 'B-Line', // Wavelynx Card Format label
  [CardFormat.WAVELYNXWALLET]: 'B-Line Wallet', // Wavelynx Wallet Card Format label
  [CardFormat.ICT]: 'ICT', // ICT Card Format label
  [CardFormat.LATCH]: 'Latch' // Latch Card Format label
};

/**  The `CardStatus` enum is defining a set of possible status values that represent different states of a
card. */
export enum CardStatus {
  UNASSIGNED = 'UNASSIGNED',
  ASSIGNED = 'ASSIGNED',
  ASSIGNED_ACTIVE = 'ASSIGNED | ACTIVE',
  ASSIGNED_INACTIVE = 'ASSIGNED | INACTIVE',
  ASSIGNED_PENDING = 'ASSIGNED | PENDING'
}

/* This is the default UTC value of 'time.Time' that Go uses as it's default value. */
export const defaultDateTimeUTCValue = '0001-01-01T00:00:00Z';

/* This is the default value of 'uuid.UUID' that Go uses as it's default value. */
export const defaultUUIDValue = '00000000-0000-0000-0000-000000000000';

/**  The values of this `assaCardTypes` are being used as options to determine the type of card for `AVL` integration. */
export const assaCardTypes = [
  { value: 'guest', label: 'Guest' }
  // { value: 'staff', label: 'Staff' } // Hide Staff cards until an alternative solution is found
];

// `Assa Visionline (AVL)` integration card type for Guest Cards
export const AssaGuestCardTypeValue = 'guest';

// `Assa Visionline (AVL)` integration card type for Override Guest Cards
export const AssaGuestOverrideCardTypeValue = 'override';

// `Assa Visionline (AVL)` integration card type for Joiner Guest Cards
export const AssaGuestJoinerCardTypeValue = 'joiner';

/**  The values of this `assaGuestCardTypes` are being used as options to determine the type of 'guest' assa card for `AVL` integration. Please don't change the order of
 * these card types as they are being used directly in some files to preload the options.  */
export const assaGuestCardTypes = [
  { value: 'override', label: 'Override' },
  { value: 'joiner', label: 'Joiner' }
];

/**  The values of `LatchKeyTypes` are being used to determine the integration key type when creating an Integration Key record for Latch integration. */
export const LatchKeyTypes = [
  { value: 'm2m', label: 'Machine-to-Machine (m2m)' },
  { value: 'passwordless', label: 'Passwordless' }
];

/**  The values of this enum `CardToggleOptions` are being used to determine the components to display in forms when the selection changes for the toggle button. */
export enum CardToggleOptions {
  NoCard = 'noCard',
  NewCard = 'newCard',
  ExistingCard = 'existingCard'
}

/**
 * Below is the error message to display when the API error is not from the server response.
 */
export const GetNonServerErrorMessage =
  'An unexpected error occurred. Please check your network and try again.';

export const LOCAL_STORAGE_KEYS = {
  aiChatSessionId: 'aiChatSessionId'
};

/**
 * Below are the Admin role options
 */
export const AdminRoleOptions = [
  { value: 'SUPER', label: 'Super' },
  { value: 'ACCOUNT', label: 'Account' },
  { value: 'BUILDING', label: 'Building' },
  { value: 'ORG', label: 'Organization' }
];

/**
 * Define constants for AIAssistant message senders
 */
export const CHAT_USER = 'user';
export const AI_CHAT_ASSISTANT = 'aiChatAssistant';

/**
 * Define the Amenity type options
 */
export const AmenityTypeOptions = [
  { value: 'ROOM', label: 'Room' },
  { value: 'DESK', label: 'Desk' },
  { value: 'PHONE BOOTH', label: 'Phone Booth' },
  { value: 'BOARDROOM', label: 'Boardroom' },
  { value: 'MEETING ROOM', label: 'Meeting Room' },
  { value: 'LAB', label: 'Lab' },
  { value: 'OFFICE', label: 'Office' },
  { value: 'COMMON AREA', label: 'Common Area' }
];

/**
 * The `ApplessOccupancySensorTypes` are the options available for appless occupancy sensor type.
 * Add more types as we start using other sensors.
 */
export const ApplessOccupancySensorTypes = [{ value: 'VSemi', label: 'VSemi' }];

export enum SpaceReportingType {
  Occupants = 'Occupants',
  People = 'People'
}

/**
 * The `AIAnalyticsType` enum is defining a set of possible tabs supported by the AIAnalytics page.
 */
export enum AIAnalyticsType {
  Feedbacks = 'Feedbacks',
  Conversations = 'Conversations'
}

/**
 * The `AIAssistantMessageDirection` enum is defining a set of possible message directions for the  AIAssistant.
 */
export enum AIAssistantMessageDirection {
  Incoming = 'incoming',
  Outgoing = 'outgoing'
}

/**
 * The `AIAssistantMessagePosition` enum is defining a set of supported message positions for the  AIAssistant.
 */
export enum AIAssistantMessagePosition {
  Normal = 'normal',
  Single = 'single',
  First = 'first',
  Last = 'last'
}
